import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Table, Cell, TableCellImageContent, TableCellTextContent, TableCellMixedContent, MixedElementImage } from '#/types/message';
import { fetchImageUrls } from '#/api/images';
import styles from './table-display.module.css';
import logger from '#/utils/logger';

interface TableDisplayProps {
  table: Table;
  onTableClick?: (table: Table) => void;
  onHeightReady?: (height: number) => void;
}

const TableDisplay: React.FC<TableDisplayProps> = ({ table, onTableClick, onHeightReady }) => {
  const [imageUrls, setImageUrls] = useState<Record<string, string>>({});
  const [loadedImageCount, setLoadedImageCount] = useState(0);
  const expectedImageCount = useRef<number | null>(null);
  const tableDisplayRef = useRef<HTMLDivElement>(null);
  const [apiCallCompleted, setApiCallCompleted] = useState(false);
  const lastHeightRef = useRef<number>(-1);
  const onHeightReadyRef = useRef(onHeightReady);
  useEffect(() => { onHeightReadyRef.current = onHeightReady; }, [onHeightReady]);

  useEffect(() => {
    if (!tableDisplayRef.current) return;
    const el = tableDisplayRef.current;
    const ro = new ResizeObserver((entries) => {
      const h = Math.ceil(entries[0].contentRect.height);
      if (h > 0 && h !== lastHeightRef.current) {
        lastHeightRef.current = h;
        onHeightReadyRef.current?.(h);
      }
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [table]);

  useEffect(() => {
    const getImageFileNames = () => {
      const fileNames: string[] = [];
      if (!table || !table.cells) return fileNames;

      table.cells.forEach(cell => {
        const cellAsImage = cell as TableCellImageContent;
        const cellAsMixed = cell as TableCellMixedContent;

        if (cell.cell_type === 'image' && cellAsImage.file_name) {
          fileNames.push(cellAsImage.file_name);
        } else if (cell.cell_type === 'mixed' && cellAsMixed.lines) {
          cellAsMixed.lines.forEach(line => {
            for (const element of line) {
              const lineAsImage = element as MixedElementImage;
              if (lineAsImage.mixed_type === 'image' && lineAsImage.file_name) {
                fileNames.push(lineAsImage.file_name);
              }
            }
          });
        }
      });
      return [...new Set(fileNames)];
    };

    const loadCellImages = async () => {
      const fileNamesToFetch = getImageFileNames();
      setApiCallCompleted(false);
      if (fileNamesToFetch.length === 0) {
        setImageUrls({});
        expectedImageCount.current = 0;
        setApiCallCompleted(true);
        return;
      }
      try {
        const urlMap = await fetchImageUrls(fileNamesToFetch);
        setImageUrls(urlMap);
        expectedImageCount.current = fileNamesToFetch.length;
        setApiCallCompleted(true);
      } catch (error: any) {
        logger.error("Failed to load images for table cells:", {
          error: error.message
        });
        setApiCallCompleted(true);
      }
    };
    setLoadedImageCount(0);
    expectedImageCount.current = null;
    loadCellImages();
  }, [table]);

  useEffect(() => {
    if (!onHeightReady || !tableDisplayRef.current) {
      return;
    }

    // Don't calculate height until API call completes
    if (!apiCallCompleted) {
      return;
    }

    // Case 1: No images
    if (expectedImageCount.current !== null && expectedImageCount.current === 0) {
      requestAnimationFrame(() => {
        if (tableDisplayRef.current) {
          onHeightReady(tableDisplayRef.current.offsetHeight);
        }
      });
      return;
    }

    // Case 2: All expected images have loaded (or errored)
    if (expectedImageCount.current !== null && loadedImageCount >= expectedImageCount.current) {
      let rafId1: number;
      let rafId2: number;
      onHeightReady(tableDisplayRef.current.offsetHeight);

      rafId1 = requestAnimationFrame(() => {
        if (!tableDisplayRef.current) return;
        rafId2 = requestAnimationFrame(() => {
          if (tableDisplayRef.current) {
            onHeightReady(tableDisplayRef.current.offsetHeight);
          }
        });
      });

      return () => {
        cancelAnimationFrame(rafId1);
        if (rafId2 !== undefined) {
          cancelAnimationFrame(rafId2);
        }
      };
    }
  }, [
    loadedImageCount,
    expectedImageCount,
    onHeightReady,
    table,
    apiCallCompleted
  ]);

  const onImageLoaded = () => {
    setLoadedImageCount(prevCount => prevCount + 1)
  }

  const onImageError = () => {
    logger.error("Failed to load image for table cell");
    setLoadedImageCount(prevCount => prevCount + 1);
  }

  const renderCellContent = (cell: Cell) => {
    const cellAsText = cell as TableCellTextContent;
    const cellAsImage = cell as TableCellImageContent;
    const cellAsMixed = cell as TableCellMixedContent;

    switch (cell.cell_type) {
      case 'text':
        return <span className={styles.tableCellTextContent}>{cellAsText.text}</span>;
      case 'image': {
        if (!apiCallCompleted) {
          return <div className={styles.imagePlaceholder}>Loading...</div>;
        }

        const imageUrl = imageUrls[cellAsImage.file_name] || cellAsImage.file_name;
        return (
          <img
            src={imageUrl}
            alt={cellAsImage.file_name || "Table cell image"}
            className={styles.tableCellImage}
            onLoad={onImageLoaded}
            onError={onImageError}
            style={{
              width: cellAsImage.width ?? 'auto',
              height: cellAsImage.height ?? 'auto',
            }}
          />
        );
      }
      case 'mixed': {
        return (
          <div className={styles.mixedContentCellContainer}>
            {cellAsMixed.lines && cellAsMixed.lines.map((lineArray, lineIndex) => {
              const lineElements = lineArray.map((element, elementIndex) => {
                if (element.mixed_type === 'text') {
                  return (
                    <span key={`line-${lineIndex}-text-${elementIndex}`} className={styles.mixedTextElement}>
                      {element.text}
                    </span>
                  );
                } else if (element.mixed_type === 'image') {
                  if (!apiCallCompleted) {
                    return <div key={`line-${lineIndex}-loading-${elementIndex}`} className={styles.imagePlaceholder}>Loading...</div>;
                  }

                  const imageElement = element as MixedElementImage;
                  const imageUrl = imageUrls[imageElement.file_name] || imageElement.file_name;
                  return (
                    <img
                      key={`line-${lineIndex}-image-${imageElement.file_name}-${elementIndex}`}
                      src={imageUrl}
                      alt={imageElement.file_name || "Mixed content image"}
                      onLoad={onImageLoaded}
                      onError={onImageError}
                      className={styles.mixedImageElement}
                      style={{
                        width: imageElement.width ?? 'auto',
                        height: imageElement.height ?? 'auto',
                        verticalAlign: 'middle',
                      }}
                    />
                  );
                }
                return null;
              });

              return (
                <div key={`line-${lineIndex}`} className={styles.mixedContentLine}>
                  {lineElements}
                </div>
              );
            })}
          </div>
        );
      }
      default:
        return null;
    }
  };

  const { numRows, numCols } = useMemo(() => {
    let maxR = 0;
    let maxC = 0;
    if (table && table.cells) {
      table.cells.forEach(c => {
        maxR = Math.max(maxR, c.row + c.row_span);
        maxC = Math.max(maxC, c.col + c.col_span);
      });
    }
    return { numRows: maxR, numCols: maxC };
  }, [table]);

  const tableGridStyle: React.CSSProperties = {
    display: 'grid',
    gridTemplateColumns: `repeat(${numCols}, minmax(30px, auto))`,
    gridTemplateRows: `repeat(${numRows}, auto)`,
  };

  if (!table) return null;

  return (
    <div
      ref={tableDisplayRef}
      className={styles.tableDisplayContainer}
      onClick={() => onTableClick?.(table)}
      role="button"
      tabIndex={0}
    >
      {table.title && <h2 className={styles.tableTitle}>{table.title}</h2>}
      {table.subtitle && <h3 className={styles.tableSubtitle}>{table.subtitle}</h3>}

      <div className={styles.tableGridWrapper} style={tableGridStyle}>
        {table.cells && table.cells.map((cell, index) => (
          <div
            key={`${cell.row}-${cell.col}-${index}`}
            className={`${styles.tableCell} ${cell.header ? styles.tableHeaderCell : ''}`}
            style={{
              gridRowStart: cell.row + 1,
              gridColumnStart: cell.col + 1,
              gridRowEnd: `span ${cell.row_span}`,
              gridColumnEnd: `span ${cell.col_span}`,
            }}
          >
            {renderCellContent(cell)}
          </div>
        ))}
      </div>

      {table.footer && <p className={styles.tableFooter}>{table.footer}</p>}
    </div>
  );
};

export default TableDisplay;
